import type {
  ChannelKind,
  Connectivity,
  DeviceStatus,
  DeviceType,
  IncidentStatus,
  IncidentType,
  Severity,
  RangeState,
} from "@/contracts";

import type { Tone } from "@/components/ui/badge";
import i18n from "@/i18n";
import {
  EM_DASH,
  formatBattery,
  formatCurrent,
  formatDistance,
  formatHumidity,
  formatTemperature,
} from "@/lib/format";

/**
 * Domain vocabulary in one place.
 *
 * The API speaks enums and the interface speaks to an operator (ADR-002). A
 * `Record` keyed by the enum means a new value fails to compile instead of
 * leaking a raw `TEMPERATURE_HIGH` onto the screen.
 */

export interface Presentation {
  label: string;
  tone: Tone;
}

export const CONNECTIVITY: Record<Connectivity, Presentation> = {
  ONLINE: {
    get label() {
      return i18n.t("devices:connectivity.ONLINE");
    },
    tone: "ok",
  },
  OFFLINE: {
    get label() {
      return i18n.t("devices:connectivity.OFFLINE");
    },
    tone: "danger",
  },
  NEVER_SEEN: {
    get label() {
      return i18n.t("devices:connectivity.NEVER_SEEN");
    },
    tone: "neutral",
  },
};

export const RANGE_STATE: Record<RangeState, Presentation> = {
  IN_RANGE: {
    get label() {
      return i18n.t("devices:range.IN_RANGE");
    },
    tone: "ok",
  },
  OUT_OF_RANGE: {
    get label() {
      return i18n.t("devices:range.OUT_OF_RANGE");
    },
    tone: "danger",
  },
  UNKNOWN: {
    get label() {
      return i18n.t("devices:range.UNKNOWN");
    },
    tone: "neutral",
  },
};

/**
 * What a sensor measures, in the words an operator would use.
 *
 * Phrased as the reading rather than the sensing technology: a reader
 * choosing hardware cares that a unit reports a level, not that it does so
 * with radar.
 */
export const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  get TEMPERATURE_HUMIDITY() {
    return i18n.t("devices:type.TEMPERATURE_HUMIDITY");
  },
  get TEMPERATURE() {
    return i18n.t("devices:type.TEMPERATURE");
  },
  get HUMIDITY() {
    return i18n.t("devices:type.HUMIDITY");
  },
  get DISTANCE_LEVEL() {
    return i18n.t("devices:type.DISTANCE_LEVEL");
  },
  get PRESENCE() {
    return i18n.t("devices:type.PRESENCE");
  },
  get WATER_LEAK() {
    return i18n.t("devices:type.WATER_LEAK");
  },
  get MAGNETIC_CONTACT() {
    return i18n.t("devices:type.MAGNETIC_CONTACT");
  },
  get IOT_CONTROLLER() {
    return i18n.t("devices:type.IOT_CONTROLLER");
  },
  get CURRENT() {
    return i18n.t("devices:type.CURRENT");
  },
};

export const CHANNEL_KIND_LABELS: Record<ChannelKind, string> = {
  get DIGITAL() {
    return i18n.t("devices:channelKind.DIGITAL");
  },
  get COUNTER() {
    return i18n.t("devices:channelKind.COUNTER");
  },
  get DIGITAL_OUTPUT() {
    return i18n.t("devices:channelKind.DIGITAL_OUTPUT");
  },
  get TEMPERATURE() {
    return i18n.t("devices:channelKind.TEMPERATURE");
  },
  get CURRENT_MA() {
    return i18n.t("devices:channelKind.CURRENT_MA");
  },
  get VOLTAGE() {
    return i18n.t("devices:channelKind.VOLTAGE");
  },
  get MODBUS() {
    return i18n.t("devices:channelKind.MODBUS");
  },
};

export const DEVICE_STATUS: Record<DeviceStatus, Presentation> = {
  PENDING: {
    get label() {
      return i18n.t("devices:status.PENDING");
    },
    tone: "warn",
  },
  ACTIVE: {
    get label() {
      return i18n.t("devices:status.ACTIVE");
    },
    tone: "ok",
  },
  INACTIVE: {
    get label() {
      return i18n.t("devices:status.INACTIVE");
    },
    tone: "neutral",
  },
  MAINTENANCE: {
    get label() {
      return i18n.t("devices:status.MAINTENANCE");
    },
    tone: "info",
  },
};

export const SEVERITY: Record<Severity, Presentation> = {
  INFO: {
    get label() {
      return i18n.t("devices:severity.INFO");
    },
    tone: "info",
  },
  WARNING: {
    get label() {
      return i18n.t("devices:severity.WARNING");
    },
    tone: "warn",
  },
  CRITICAL: {
    get label() {
      return i18n.t("devices:severity.CRITICAL");
    },
    tone: "danger",
  },
};

export const INCIDENT_STATUS: Record<IncidentStatus, Presentation> = {
  OPEN: {
    get label() {
      return i18n.t("devices:incidentStatus.OPEN");
    },
    tone: "danger",
  },
  ACKNOWLEDGED: {
    get label() {
      return i18n.t("devices:incidentStatus.ACKNOWLEDGED");
    },
    tone: "warn",
  },
  RESOLVED: {
    get label() {
      return i18n.t("devices:incidentStatus.RESOLVED");
    },
    tone: "ok",
  },
};

export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  get TEMPERATURE_HIGH() {
    return i18n.t("devices:incidentType.TEMPERATURE_HIGH");
  },
  get TEMPERATURE_LOW() {
    return i18n.t("devices:incidentType.TEMPERATURE_LOW");
  },
  get HUMIDITY_HIGH() {
    return i18n.t("devices:incidentType.HUMIDITY_HIGH");
  },
  get HUMIDITY_LOW() {
    return i18n.t("devices:incidentType.HUMIDITY_LOW");
  },
  get BATTERY_LOW() {
    return i18n.t("devices:incidentType.BATTERY_LOW");
  },
  get DEVICE_OFFLINE() {
    return i18n.t("devices:incidentType.DEVICE_OFFLINE");
  },
  get DISTANCE_HIGH() {
    return i18n.t("devices:incidentType.DISTANCE_HIGH");
  },
  get DISTANCE_LOW() {
    return i18n.t("devices:incidentType.DISTANCE_LOW");
  },
  get PRESENCE_UNEXPECTED() {
    return i18n.t("devices:incidentType.PRESENCE_UNEXPECTED");
  },
  get LEAK_DETECTED() {
    return i18n.t("devices:incidentType.LEAK_DETECTED");
  },
  get DOOR_OPEN() {
    return i18n.t("devices:incidentType.DOOR_OPEN");
  },
  get CHANNEL_HIGH() {
    return i18n.t("devices:incidentType.CHANNEL_HIGH");
  },
  get CHANNEL_LOW() {
    return i18n.t("devices:incidentType.CHANNEL_LOW");
  },
  get CHANNEL_ACTIVE() {
    return i18n.t("devices:incidentType.CHANNEL_ACTIVE");
  },
  get CURRENT_HIGH() {
    return i18n.t("devices:incidentType.CURRENT_HIGH");
  },
  get CURRENT_LOW() {
    return i18n.t("devices:incidentType.CURRENT_LOW");
  },
};

/** Columns shared by the dashboard table and the full device list. */
export const DEVICE_TABLE_COLUMNS: readonly { label: string; numeric: boolean }[] = [
  {
    get label() {
      return i18n.t("devices:table.asset");
    },
    numeric: false,
  },
  {
    get label() {
      return i18n.t("devices:table.site");
    },
    numeric: false,
  },
  {
    get label() {
      return i18n.t("devices:table.connectivity");
    },
    numeric: false,
  },
  {
    get label() {
      return i18n.t("devices:table.temperature");
    },
    numeric: true,
  },
  {
    get label() {
      return i18n.t("devices:table.humidity");
    },
    numeric: true,
  },
  {
    get label() {
      return i18n.t("devices:table.distance");
    },
    numeric: true,
  },
  {
    get label() {
      return i18n.t("devices:table.current");
    },
    numeric: true,
  },
  {
    get label() {
      return i18n.t("devices:table.battery");
    },
    numeric: true,
  },
  {
    get label() {
      return i18n.t("devices:table.lastReading");
    },
    numeric: false,
  },
];

/**
 * The reading that opened an incident, in its own unit.
 *
 * The API stores one `triggerValue` for every incident type, so the unit has
 * to come from the type: showing a battery percentage as "12.0 °C" would be
 * worse than showing nothing.
 */
export function formatIncidentValue(type: IncidentType, value: number | null): string {
  if (value === null) {
    return EM_DASH;
  }

  switch (type) {
    case "TEMPERATURE_HIGH":
    case "TEMPERATURE_LOW":
      return formatTemperature(value);
    case "HUMIDITY_HIGH":
    case "HUMIDITY_LOW":
      return formatHumidity(value);
    case "DISTANCE_HIGH":
    case "DISTANCE_LOW":
      return formatDistance(value);
    case "CURRENT_HIGH":
    case "CURRENT_LOW":
      return formatCurrent(value);
    case "BATTERY_LOW":
      return formatBattery(value);
    // Neither carries a value: one is the absence of readings, the other is
    // a bit, and there is no number a reader could compare to a threshold.
    case "DEVICE_OFFLINE":
    case "PRESENCE_UNEXPECTED":
    case "LEAK_DETECTED":
    case "DOOR_OPEN":
    case "CHANNEL_ACTIVE":
      return EM_DASH;
    case "CHANNEL_HIGH":
    case "CHANNEL_LOW":
      return String(value);
  }
}
